import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API } from "@/lib/auth";
import { useDemoBrand } from "@/lib/demoBrand";

const FIELDS = [
  { key: "full_name", label: "Full name", type: "text", auto: "name" },
  { key: "organization", label: "Organization", type: "text", auto: "organization" },
  { key: "role", label: "Role / title", type: "text", auto: "organization-title" },
  { key: "email", label: "Email", type: "email", auto: "email" },
  { key: "phone", label: "Phone", type: "tel", auto: "tel" },
  { key: "region", label: "County or region", type: "text", auto: "address-level2" },
];
const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

export default function ExchangePilotForm() {
  const { slug } = useDemoBrand();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      let share_token = null;
      try { share_token = JSON.parse(sessionStorage.getItem(`demo_share_${slug}`) || "null")?.token || null; } catch {}
      await axios.post(`${API}/demos/${slug}/pilot-request`, { ...form, share_token });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? "Please fill every field with a valid email and phone." : d || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div data-testid="exchange-pilot-success" className="mt-6 flex items-start gap-3 rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/10 p-5 animate-in fade-in duration-300">
      <CheckCircle2 className="w-5 h-5 text-[#F4D88A] shrink-0 mt-0.5" />
      <div>
        <div className="text-white font-semibold">Your pilot request is in.</div>
        <p className="text-sm text-slate-300 mt-1">Our team will reach out within one business day to schedule your two-week launch.</p>
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="exchange-pilot-form" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {FIELDS.map((f) => (
        <input key={f.key} required type={f.type} autoComplete={f.auto} placeholder={f.label} value={form[f.key]}
          onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
          data-testid={`exchange-pilot-${f.key.replace("_", "-")}`}
          className="rounded-xl bg-white/[0.04] border border-[#D4AF37]/25 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37] transition-colors" />
      ))}
      {error && <div data-testid="exchange-pilot-error" className="sm:col-span-2 text-xs text-rose-300">{error}</div>}
      <button type="submit" disabled={busy} data-testid="exchange-pilot-submit"
        className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#C9A227] to-[#F4D88A] px-6 py-3 font-bold text-[#0A0612] hover:brightness-110 active:scale-[0.98] transition-[filter,transform] disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Request my 90-day pilot
      </button>
    </form>
  );
}
